import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { viewportBandForWidth, type ShellViewportBand } from '../navigation/viewportBand';
import RecordsDisclaimer from '@/features/business/RecordsDisclaimer';
import { ClientSwitcher } from './ClientSwitcher';

type AccountantShellProps = {
  children: ReactNode;
  heading?: string;
  clientSwitcher?: ReactNode;
  onClientSwitch?: () => void;
};

type AccountantDestination = {
  suffix: 'review' | 'open-items' | 'schedule-c' | 'exports';
  desktopLabel: string;
  mobileLabel: string;
  icon: string;
};

const DESTINATIONS: AccountantDestination[] = [
  { suffix: 'review', desktopLabel: 'Business review', mobileLabel: 'Review', icon: '▤' },
  { suffix: 'open-items', desktopLabel: 'Open items', mobileLabel: 'Open items', icon: '!' },
  { suffix: 'schedule-c', desktopLabel: 'Schedule C', mobileLabel: 'Schedule C', icon: '∑' },
  { suffix: 'exports', desktopLabel: 'Export package', mobileLabel: 'Exports', icon: '↥' },
];

function useViewportBand(): ShellViewportBand {
  const [band, setBand] = useState<ShellViewportBand>(() =>
    typeof window === 'undefined' ? 'desktop' : viewportBandForWidth(window.innerWidth),
  );

  useEffect(() => {
    const onResize = () => setBand(viewportBandForWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return band;
}

function destinationHref(grantId: string | undefined, taxYear: string | undefined, suffix: AccountantDestination['suffix']): string {
  if (!grantId || !taxYear) return '/accountant/invitation';
  return `/accountant/clients/${grantId}/${taxYear}/${suffix}`;
}

function AccountantLinks({
  band,
  grantId,
  taxYear,
  mobile = false,
}: {
  band: ShellViewportBand;
  grantId?: string;
  taxYear?: string;
  mobile?: boolean;
}) {
  return (
    <nav aria-label="Accountant workspace" className={mobile ? 'flex w-full items-stretch justify-around' : 'flex flex-col gap-1'}>
      {DESTINATIONS.map((destination) => (
        <NavLink
          key={destination.suffix}
          to={destinationHref(grantId, taxYear, destination.suffix)}
          aria-label={mobile ? destination.mobileLabel : destination.desktopLabel}
          className={({ isActive }) => [
            'flex min-h-11 items-center rounded-md text-sm transition-colors',
            mobile ? 'min-w-11 flex-1 flex-col justify-center gap-0.5 px-1 text-xs' : 'gap-3 px-3',
            band === 'tablet' && !mobile ? 'w-12 flex-col justify-center gap-0.5 px-1 text-[10px]' : '',
            isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          ].join(' ')}
        >
          <span aria-hidden="true" className="text-base leading-none">{destination.icon}</span>
          <span>{mobile ? destination.mobileLabel : destination.desktopLabel}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function ClientBoundary({ onClientSwitch, compact = false }: { onClientSwitch?: () => void; compact?: boolean }) {
  return (
    <div className={compact ? 'flex items-center gap-2' : 'rounded-lg border bg-card p-3'} data-testid="accountant-client-boundary">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">Accountant workspace</p>
        <p className="text-xs text-muted-foreground">Business only</p>
      </div>
      <button
        type="button"
        className="min-h-11 rounded-md px-2 text-xs text-foreground underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onClientSwitch}
      >
        Switch client
      </button>
    </div>
  );
}

export function AccountantShell({ children, heading = 'Accountant workspace', clientSwitcher, onClientSwitch }: AccountantShellProps) {
  const band = useViewportBand();
  const location = useLocation();
  const { grantId, taxYear } = useParams<{ grantId: string; taxYear: string }>();
  const pathParts = location.pathname.split('/');
  const resolvedGrantId = grantId ?? pathParts[3];
  const resolvedTaxYear = taxYear ?? pathParts[4];

  useEffect(() => {
    const headingElement = document.querySelector('main[data-testid="shell-main"] h1');
    if (headingElement instanceof HTMLElement) {
      headingElement.tabIndex = -1;
      headingElement.focus({ preventScroll: true });
    }
  }, [location.pathname]);

  const switcher = clientSwitcher ?? (
    <ClientSwitcher currentGrantId={resolvedGrantId} currentTaxYear={resolvedTaxYear ? Number(resolvedTaxYear) : undefined} onClientSwitch={onClientSwitch} />
  );

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground" data-testid="accountant-shell" data-viewport-band={band}>
      {band === 'mobile' && (
        <header className="border-b bg-background px-4 py-3" data-testid="accountant-mobile-header">
          <ClientBoundary compact onClientSwitch={onClientSwitch} />
        </header>
      )}
      <div className="flex min-h-0 flex-1">
        {band === 'desktop' && (
          <aside className="flex w-[220px] shrink-0 flex-col border-r bg-sidebar p-4" data-testid="accountant-desktop-sidebar">
            <div className="mb-6">
              <p className="text-base font-semibold">MoneySkyline</p>
              <p className="text-xs text-muted-foreground">Accountant</p>
            </div>
            {switcher}
            <div className="mt-6">
              <AccountantLinks band={band} grantId={resolvedGrantId} taxYear={resolvedTaxYear} />
            </div>
            <div className="mt-auto pt-6">
              <RecordsDisclaimer />
            </div>
          </aside>
        )}
        {band === 'tablet' && (
          <aside className="flex w-[84px] shrink-0 flex-col items-center gap-5 border-r bg-sidebar py-4" data-testid="accountant-tablet-rail">
            <span className="text-sm font-semibold" aria-label="Accountant">MS</span>
            <span className="rounded-full border px-2 py-1 text-[10px] text-muted-foreground">Business only</span>
            <AccountantLinks band={band} grantId={resolvedGrantId} taxYear={resolvedTaxYear} />
          </aside>
        )}
        <main className="min-w-0 flex-1 overflow-y-auto" data-testid="shell-main">
          <div className="mx-auto flex min-h-full max-w-7xl flex-col px-4 py-6 pb-24 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h1 tabIndex={-1} className="text-2xl font-semibold tracking-tight focus:outline-none">{heading}</h1>
                <p className="mt-1 text-sm text-muted-foreground">Business-only records for the selected tax year.</p>
              </div>
              {band === 'tablet' && <div className="hidden sm:block">{switcher}</div>}
            </div>
            {children}
            {band !== 'desktop' && <RecordsDisclaimer className="mt-auto pt-8" />}
          </div>
        </main>
      </div>
      {band === 'mobile' && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background pb-[env(safe-area-inset-bottom)]" data-testid="accountant-mobile-nav">
          <AccountantLinks band={band} grantId={resolvedGrantId} taxYear={resolvedTaxYear} mobile />
        </div>
      )}
    </div>
  );
}

export default AccountantShell;
